/**
 * Maintenance Records Modal Component
 * Create and view maintenance records for IT Assets
 */
import React, { useState, useEffect, useCallback } from 'react';
import { X, Wrench, Plus, CheckCircle, Clock, AlertTriangle, Calendar, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateOnly } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('token');

const apiCall = async (endpoint, options = {}) => {
  const token = getToken();
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

// Status Badge Component
const MaintenanceStatusBadge = ({ status }) => {
  const statusConfig = {
    scheduled: { class: 'maint-scheduled', label: 'Scheduled', icon: Calendar },
    in_progress: { class: 'maint-in-progress', label: 'In Progress', icon: Clock },
    completed: { class: 'maint-completed', label: 'Completed', icon: CheckCircle },
    cancelled: { class: 'maint-cancelled', label: 'Cancelled', icon: X }
  };
  
  const config = statusConfig[status] || statusConfig.scheduled;
  const Icon = config.icon;
  
  return (
    <span className={`maint-status-badge ${config.class}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Create Maintenance Form Component
const CreateMaintenanceForm = ({ asset, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    maintenance_type: 'repair',
    description: '',
    scheduled_date: '',
    vendor: '',
    estimated_cost: '',
    ticket_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const maintenanceTypes = [
    { value: 'repair', label: 'Repair' },
    { value: 'upgrade', label: 'Upgrade' },
    { value: 'inspection', label: 'Inspection' },
    { value: 'cleaning', label: 'Cleaning' },
    { value: 'replacement', label: 'Part Replacement' },
    { value: 'other', label: 'Other' }
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        asset_id: asset.id,
        maintenance_type: formData.maintenance_type,
        description: formData.description,
        scheduled_date: formData.scheduled_date || null,
        vendor: formData.vendor || null,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        ticket_id: formData.ticket_id || null
      };
      
      await onSubmit(payload);
      toast.success('Maintenance record created');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="maintenance-form">
      <div className="asset-info-header">
        <Wrench size={16} />
        <span>Schedule Maintenance for <strong>{asset.name}</strong></span>
        <code className="asset-tag-mini">{asset.asset_tag}</code>
      </div>
      
      <div className="form-group">
        <label>Maintenance Type *</label>
        <select 
          value={formData.maintenance_type} 
          onChange={e => setFormData({...formData, maintenance_type: e.target.value})}
          required
        >
          {maintenanceTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>
      
      <div className="form-group">
        <label>Description *</label>
        <textarea
          value={formData.description}
          onChange={e => setFormData({...formData, description: e.target.value})}
          placeholder="Describe the maintenance work needed..."
          rows={3}
          required
          minLength={10}
        />
      </div>
      
      <div className="form-row">
        <div className="form-group">
          <label>Scheduled Date</label>
          <input
            type="date"
            value={formData.scheduled_date}
            onChange={e => setFormData({...formData, scheduled_date: e.target.value})}
            min={new Date().toISOString().split('T')[0]}
          />
        </div>
        <div className="form-group">
          <label>Estimated Cost ($)</label>
          <input
            type="number"
            step="0.01"
            value={formData.estimated_cost}
            onChange={e => setFormData({...formData, estimated_cost: e.target.value})}
            placeholder="0.00"
          />
        </div>
      </div>
      
      <div className="form-group">
        <label>Vendor/Service Provider</label>
        <input
          type="text"
          value={formData.vendor}
          onChange={e => setFormData({...formData, vendor: e.target.value})}
          placeholder="e.g., Dell Support, Local IT Shop"
        />
      </div>
      
      <div className="form-group">
        <label>Link to IT Ticket (Optional)</label>
        <input
          type="text"
          value={formData.ticket_id}
          onChange={e => setFormData({...formData, ticket_id: e.target.value})}
          placeholder="Ticket ID if related to a support request"
        />
      </div>
      
      <div className="modal-actions">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary" disabled={isSubmitting || !formData.description}>
          {isSubmitting ? 'Creating...' : 'Schedule Maintenance'}
        </button>
      </div>
    </form>
  );
};

// Maintenance Records List Component
const MaintenanceList = ({ records, onUpdate, isITManager }) => {
  const [updatingId, setUpdatingId] = useState(null);
  
  const handleStatusChange = async (recordId, newStatus, actualCost = null) => {
    setUpdatingId(recordId);
    try {
      const updateData = { status: newStatus };
      if (actualCost !== null) {
        updateData.actual_cost = actualCost;
      }
      if (newStatus === 'completed') {
        updateData.completion_date = new Date().toISOString().split('T')[0];
      }
      await onUpdate(recordId, updateData);
      toast.success(`Maintenance ${newStatus === 'completed' ? 'completed' : 'updated'}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdatingId(null);
    }
  };
  
  if (records.length === 0) {
    return (
      <div className="empty-maintenance">
        <Wrench size={32} />
        <p>No maintenance records</p>
      </div>
    );
  }
  
  return (
    <div className="maintenance-list">
      {records.map(record => (
        <div key={record.id} className={`maintenance-record status-${record.status}`}>
          <div className="maint-header">
            <span className="maint-type">{record.maintenance_type}</span>
            <MaintenanceStatusBadge status={record.status} />
          </div>
          
          <div className="maint-asset-info">
            <code>{record.asset_tag}</code>
            <span>{record.asset_name}</span>
          </div>
          
          <p className="maint-description">{record.description}</p>
          
          <div className="maint-details">
            {record.scheduled_date && (
              <span><Calendar size={12} /> {record.scheduled_date}</span>
            )}
            {record.vendor && (
              <span>Vendor: {record.vendor}</span>
            )}
            {record.estimated_cost && (
              <span><DollarSign size={12} /> Est: ${record.estimated_cost}</span>
            )}
            {record.actual_cost && (
              <span><DollarSign size={12} /> Actual: ${record.actual_cost}</span>
            )}
          </div>
          
          <div className="maint-footer">
            <span className="maint-meta">By {record.created_by_name} • {formatDateOnly(record.created_at)}</span>
            
            {record.status !== 'completed' && record.status !== 'cancelled' && (
              <div className="maint-actions">
                {record.status === 'scheduled' && (
                  <button 
                    className="btn-small btn-progress"
                    onClick={() => handleStatusChange(record.id, 'in_progress')}
                    disabled={updatingId === record.id}
                  >
                    Start
                  </button>
                )}
                <button 
                  className="btn-small btn-complete"
                  onClick={() => handleStatusChange(record.id, 'completed')}
                  disabled={updatingId === record.id}
                >
                  Complete
                </button>
                {isITManager && (
                  <button 
                    className="btn-small btn-cancel"
                    onClick={() => handleStatusChange(record.id, 'cancelled')}
                    disabled={updatingId === record.id}
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Main Maintenance Modal Component
const MaintenanceModal = ({ isOpen, onClose, asset, isITManager }) => {
  const [view, setView] = useState('list'); // 'list' or 'create'
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const fetchRecords = useCallback(async () => {
    if (!asset) return;
    setLoading(true);
    try {
      const data = await apiCall(`/api/it-assets/maintenance?asset_id=${asset.id}`);
      setRecords(data.records || []);
    } catch (error) {
      console.error('Failed to fetch maintenance records:', error);
    } finally {
      setLoading(false);
    }
  }, [asset]);
  
  useEffect(() => {
    if (isOpen && asset) {
      fetchRecords();
      setView('list');
    }
  }, [isOpen, asset, fetchRecords]);
  
  const handleCreate = async (payload) => {
    await apiCall('/api/it-assets/maintenance', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    setView('list');
    fetchRecords();
  };
  
  const handleUpdate = async (recordId, updateData) => {
    await apiCall(`/api/it-assets/maintenance/${recordId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
    fetchRecords();
  };
  
  if (!isOpen || !asset) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large maintenance-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-row">
            <Wrench size={18} />
            <h3>Maintenance Records</h3>
            <code className="asset-tag-badge">{asset.asset_tag}</code>
          </div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        
        <div className="modal-body">
          {view === 'list' ? (
            <>
              <div className="maintenance-header">
                <h4>{asset.name}</h4>
                <button className="btn-action" onClick={() => setView('create')}>
                  <Plus size={14} /> Schedule Maintenance
                </button>
              </div>
              
              {loading ? (
                <div className="loading-state">Loading...</div>
              ) : (
                <MaintenanceList 
                  records={records} 
                  onUpdate={handleUpdate}
                  isITManager={isITManager}
                />
              )}
            </>
          ) : (
            <CreateMaintenanceForm 
              asset={asset}
              onSubmit={handleCreate}
              onCancel={() => setView('list')}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceModal;
